<template>
  <div class="flex flex-col" :class="isUser ? 'items-start' : 'items-end'">
    <div class="flex items-center gap-1.5 mb-1">
      <span class="inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-medium" :class="authorBadgeClass">
        {{ authorLabel }}
      </span>
      <span
        v-if="message.isNote"
        class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-xs font-medium bg-yellow-100 text-yellow-800"
      >
        <LockClosedIcon class="h-3 w-3" />
        {{ t("internal_note") }}
      </span>
      <span class="text-xs text-gray-500">{{ formattedDate }}</span>
    </div>
    <div class="rounded-xl px-4 py-2 text-sm" :class="bubbleClass">
      <div v-html="sanitizedBody"></div>
      <div v-if="message.attachments?.length" class="mt-2 space-y-1">
        <IntercomAttachmentLink
          v-for="(attachment, index) in message.attachments"
          :key="index"
          :attachment="attachment"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import DOMPurify from "dompurify";
import { LockClosedIcon } from "@heroicons/vue/solid";
import IntercomAttachmentLink from "./IntercomAttachmentLink.vue";

const PARAHELP_ADMIN_ID = process.env.VUE_APP_PARAHELP_ADMIN_ID || "11224489";

export default {
  name: "IntercomMessageBubble",

  components: { IntercomAttachmentLink, LockClosedIcon },

  props: {
    message: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },

  computed: {
    isUser() {
      return this.message.author?.type === "user";
    },

    isParahelp() {
      return String(this.message.author?.id) === PARAHELP_ADMIN_ID;
    },

    authorLabel() {
      if (this.isUser) return this.t("client");
      if (this.isParahelp) return this.t("ai");
      return this.t("team");
    },

    authorBadgeClass() {
      if (this.isUser) return "bg-blue-100 text-blue-800";
      if (this.isParahelp) return "bg-purple-100 text-purple-800";
      return "bg-green-100 text-green-800";
    },

    bubbleClass() {
      if (this.message.isNote) return "bg-yellow-50 text-gray-900 border border-yellow-200";
      if (this.isUser) return "bg-gray-100 text-gray-900";
      return "bg-primary-light text-gray-900";
    },

    sanitizedBody() {
      return DOMPurify.sanitize(this.message.body || "");
    },

    formattedDate() {
      if (!this.message.createdAt) return "";
      return new Date(this.message.createdAt).toLocaleString(this.locale);
    },
  },
};
</script>

<i18n src="./IntercomMessageBubble.i18n.json"></i18n>
